import {
  type ComponentType,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Controller,
  FormProvider,
  useForm,
  type FieldErrors,
} from "react-hook-form";
import { Box, Grid, Typography } from "@mui/material";
import type {
  ProcessedFormField,
  ScopeBundle,
} from "@/types/api/formSchema";
import { useFormulaFields } from "@/hooks/useFormulaFields";
import {
  buildConditionalFieldNames,
  buildDefaultValues,
  buildFieldRules,
  stripConditionallyHiddenFields,
} from "../utils/schemaUtils";
import { usePersistedFormData } from "../hooks/usePersistedFormData";
import { HydrationContext } from "../hooks/HydrationContext";
import { formatValue } from "../utils/fieldFormat";
import FieldGroup from "./FieldGroup";
import { resolveTooltip } from "../utils/fieldTooltips";
import {
  UnlockedFieldsContext,
  type UnlockedFields,
  type UnlockOptions,
} from "../hooks/UnlockedFieldsContext";
import { TextField } from "../fields/TextField";
import { TextareaField } from "../fields/TextareaField";
import { SelectField } from "../fields/SelectField";
import { RadioField } from "../fields/RadioField";
import { CheckboxField } from "../fields/CheckboxField";
import { BooleanField } from "../fields/BooleanField";
import { EmailsField } from "../fields/EmailsField";
import { DateField } from "../fields/DateField";
import { PhoneField } from "../fields/PhoneField";
import {
  AttachmentField,
  AttachmentOwnerContext,
} from "../fields/AttachmentField";

export type SchemaRendererHandle = {
  submit: () => void;
  reset: () => void;
  getValues: () => Record<string, unknown>;
  isDirty: () => boolean;
  markSaved: () => void;
  setFieldErrors: (errors: Record<string, string>) => void;
  // Implemented by BranchesRepeater (which uses this handle type for its own
  // ref). SchemaRenderer itself doesn't provide these — callers must use
  // optional chaining. Left optional so a SchemaRenderer-backed ref stays
  // type-safe while BranchesRepeater keeps the widened contract.
  setRowErrors?: (errors: Record<number, string>) => void;
  setSectionError?: (message: string | null) => void;
};

type Props = {
  fields: ProcessedFormField[];
  onSubmit: (data: Record<string, unknown>) => void | Promise<void>;
  initialValues?: Record<string, unknown>;
  readOnlyFields?: Set<string>;
  hiddenFieldTypes?: Set<string>;
  scope?: ScopeBundle;
  attachmentOwner?: {
    ownerRef?: string;
    ownerType?: string;
    authMode?: "vendor" | "reviewer";
  };
  columns?: 1 | 2;
  header?: React.ReactNode;
  sections?: import("@/types/api/formSchema").FormMetadataSection[];
  sectionExtras?: Record<string, React.ReactNode>;
  persistKey?: string | null;
  /** Invisible components rendered inside the FormProvider — use to hook
   * side-effects like watch-and-populate (e.g. PAN -> Legal Name autofill). */
  formEffects?: React.ReactNode;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const FIELD_RENDERERS: Record<string, ComponentType<any>> = {
  text: TextField,
  email: TextField,
  emails: EmailsField,
  link: TextField,
  textarea: TextareaField,
  select: SelectField,
  radio: RadioField,
  checkbox: CheckboxField,
  boolean: BooleanField,
  date: DateField,
  phone: PhoneField,
  attachment: AttachmentField,
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const FULL_WIDTH_TYPES = new Set([
  "radio",
  "checkbox",
  "boolean",
  "textarea",
  "attachment",
]);

// Fields that render at half-width even under columns=1, so they pair up
// on the same row (e.g. UDYAM Number + MSME Date of Registration on step 1).
const HALF_WIDTH_NAMES = new Set([
  "msme_registration_number",
  "msme_registration_date",
]);

export const SchemaRenderer = forwardRef<SchemaRendererHandle, Props>(
  function SchemaRenderer(
    {
      fields,
      onSubmit,
      initialValues,
      readOnlyFields,
      hiddenFieldTypes,
      scope,
      attachmentOwner,
      columns = 2,
      header,
      sections,
      sectionExtras,
      persistKey,
      formEffects,
    },
    ref,
  ) {
    const defaults = useMemo(
      () => buildDefaultValues(fields, initialValues),
      [fields, initialValues],
    );

    const form = useForm({
      defaultValues: defaults,
      mode: "onBlur",
    });

    useEffect(() => {
      // Only reset when the form is pristine — otherwise a caller that passes
      // a non-memoized `initialValues={{…}}` object recomputes `defaults`
      // every render and wipes the user's in-flight typed input.
      if (form.formState.isDirty) return;
      form.reset(defaults);
    }, [defaults, form]);

    const hydrationCount = usePersistedFormData(persistKey ?? null, form);

    const watchedValues = form.watch();

    const { visibleFields, readOnlyFieldNames, computedValues } =
      useFormulaFields(fields, scope, watchedValues);


    const computedFieldDefs = useMemo(
      () => fields.filter((f) => typeof f.value === "string" && !f.prefilled),
      [fields],
    );

    const syncing = useRef(new Set<string>());
    const writeCounts = useRef(new Map<string, number>());
    const cyclesLogged = useRef(new Set<string>());

    useEffect(() => {
      if (!computedFieldDefs.length) return;

      const MAX_WRITES_PER_TICK = 5;

      for (const field of computedFieldDefs) {
        if (syncing.current.has(field.name)) continue;
        const computed = computedValues[field.name];
        const effectiveVal =
          computed !== undefined ? computed : (defaults[field.name] ?? "");
        const current = form.getValues(field.name);
        const shouldWrite =
          !Object.is(effectiveVal, current) &&
          !(typeof effectiveVal === "number" && !Number.isFinite(effectiveVal));

        if (!shouldWrite) {
          writeCounts.current.delete(field.name);
          continue;
        }

        const count = (writeCounts.current.get(field.name) ?? 0) + 1;
        writeCounts.current.set(field.name, count);
        if (count > MAX_WRITES_PER_TICK) {
          if (!cyclesLogged.current.has(field.name)) {
            cyclesLogged.current.add(field.name);
            if (process.env.NODE_ENV !== "production") {
              console.warn(
                `[SchemaRenderer] Cycle detected on computed field "${field.name}"; halting further writes for this field until its effective value stabilizes.`,
              );
            }
          }
          continue;
        }

        syncing.current.add(field.name);
        form.setValue(field.name, effectiveVal, { shouldValidate: false });
        queueMicrotask(() => syncing.current.delete(field.name));
      }
    }, [computedValues, computedFieldDefs, defaults, form]);

    // Fields unlocked by a side-effect component (see UnlockedFieldsContext).
    const [unlocked, setUnlockedState] = useState<ReadonlyMap<string, UnlockOptions>>(
      () => new Map<string, UnlockOptions>(),
    );
    const setFieldUnlocked = useCallback(
      (name: string, on: boolean, options?: UnlockOptions) => {
        setUnlockedState((prev) => {
          if (!on) {
            if (!prev.has(name)) return prev;
            const next = new Map(prev);
            next.delete(name);
            return next;
          }
          const current = prev.get(name);
          if (current && current.helperText === options?.helperText) return prev;
          const next = new Map(prev);
          next.set(name, options ?? {});
          return next;
        });
      },
      [],
    );
    const unlockedCtx = useMemo<UnlockedFields>(
      () => ({ unlocked, setFieldUnlocked }),
      [unlocked, setFieldUnlocked],
    );

    const mergedReadOnlyFields = useMemo(() => {
      if (!readOnlyFields?.size && !readOnlyFieldNames.size) return undefined;
      const merged = new Set(readOnlyFields);
      for (const name of readOnlyFieldNames) merged.add(name);
      return merged;
    }, [readOnlyFields, readOnlyFieldNames]);

    const formContainerRef = useRef<HTMLDivElement>(null);

    const scrollToFirstError = useCallback(
      (errors: FieldErrors) => {
        const errorKeys = Object.keys(errors);
        if (errorKeys.length === 0) return;

        const firstErrorField = visibleFields.find((f) =>
          errorKeys.includes(f.name),
        );
        if (!firstErrorField) return;

        const el = formContainerRef.current?.querySelector<HTMLElement>(
          `[data-field-name="${CSS.escape(firstErrorField.name)}"]`,
        );
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      },
      [visibleFields],
    );

    const checkDirty = useCallback((): boolean => {
      const current = form.getValues();
      for (const key of Object.keys(defaults)) {
        const cur = current[key];
        const def = defaults[key];
        if (typeof def === "object" && def !== null) {
          if (JSON.stringify(cur) !== JSON.stringify(def)) return true;
        } else if (cur !== def) {
          return true;
        }
      }
      return false;
    }, [form, defaults]);

    const conditionalFieldNames = useMemo(
      () => buildConditionalFieldNames(fields),
      [fields],
    );

    const computedFieldNames = useMemo(
      () => new Set(computedFieldDefs.map((f) => f.name)),
      [computedFieldDefs],
    );

    const visibleFieldNames = useMemo(
      () => new Set(visibleFields.map((f) => f.name)),
      [visibleFields],
    );

    const doStripHiddenFields = useCallback(
      (data: Record<string, unknown>) =>
        stripConditionallyHiddenFields(
          data,
          visibleFieldNames,
          conditionalFieldNames,
          computedFieldNames,
        ),
      [visibleFieldNames, conditionalFieldNames, computedFieldNames],
    );

    // Conditionally-hidden fields must clear on visibility flip — no carry-over.
    // The syncing ref mirrors the computed-field cycle guard above: a
    // form.setValue() triggers a watch → visibleFields recompute → this
    // effect re-fires. Deferring the delete of the syncing marker to the
    // next microtask stops the re-entrant clear from firing before React
    // has processed the just-issued update.
    const clearingHidden = useRef(new Set<string>());
    useEffect(() => {
      for (const name of conditionalFieldNames) {
        if (visibleFieldNames.has(name) || computedFieldNames.has(name))
          continue;
        if (clearingHidden.current.has(name)) continue;
        const current = form.getValues(name);
        const isEmpty =
          current == null ||
          current === "" ||
          (Array.isArray(current) && current.length === 0);
        if (!isEmpty) {
          clearingHidden.current.add(name);
          form.setValue(name, undefined, {
            shouldDirty: false,
            shouldValidate: false,
          });
          queueMicrotask(() => clearingHidden.current.delete(name));
        }
      }
    }, [visibleFieldNames, conditionalFieldNames, computedFieldNames, form]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        form.clearErrors();
        void form.handleSubmit(
          (data) => onSubmit(doStripHiddenFields(data)),
          scrollToFirstError,
        )();
      },
      reset: () => {
        form.reset();
      },
      getValues: () => form.getValues(),
      isDirty: checkDirty,
      markSaved: () => {
        form.reset(form.getValues(), {
          keepValues: true,
          keepDirty: false,
          keepDirtyValues: false,
        });
      },
      setFieldErrors: (errors) => {
        for (const [name, message] of Object.entries(errors)) {
          form.setError(name, { type: "server", message });
        }
      },
    }));

    return (
      <FormProvider {...form}>
        <HydrationContext.Provider value={hydrationCount}>
        <AttachmentOwnerContext.Provider value={attachmentOwner ?? {}}>
        <UnlockedFieldsContext.Provider value={unlockedCtx}>
        {formEffects}
        <form
          onSubmit={form.handleSubmit(
            (data) => onSubmit(doStripHiddenFields(data)),
            scrollToFirstError,
          )}
        >
          <Box ref={formContainerRef}>
            {header}
            {sections && sections.length > 0
              ? renderSectioned({
                  fields: visibleFields,
                  sections,
                  sectionExtras,
                  form,
                  hiddenFieldTypes,
                  mergedReadOnlyFields,
                  unlockedFields: unlocked,
                  columns,
                })
              : renderFlatGrid({
                  fields: visibleFields,
                  form,
                  hiddenFieldTypes,
                  mergedReadOnlyFields,
                  unlockedFields: unlocked,
                  columns,
                })}
          </Box>
        </form>
        </UnlockedFieldsContext.Provider>
        </AttachmentOwnerContext.Provider>
        </HydrationContext.Provider>
      </FormProvider>
    );
  },
);

type ResolvedField = ProcessedFormField & {
  hidden?: boolean;
  required?: boolean | string;
  disabled?: boolean;
};

type RenderCommon = {
  fields: ResolvedField[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  hiddenFieldTypes: Set<string> | undefined;
  mergedReadOnlyFields: Set<string> | undefined;
  unlockedFields?: ReadonlyMap<string, UnlockOptions>;
  columns: 1 | 2;
};

function fieldItem(
  rawField: ResolvedField,
  { form, hiddenFieldTypes, mergedReadOnlyFields, unlockedFields, columns }: RenderCommon,
): React.ReactNode {
  // Unlocked by its source component → editable this render (see UnlockedFieldsContext).
  const unlock = unlockedFields?.get(rawField.name);
  const schemaField: ResolvedField = unlock
    ? {
        ...rawField,
        disabled: false,
        helperText: unlock.helperText ?? "Couldn't be filled automatically just now — enter it manually.",
      }
    : rawField;
  if (hiddenFieldTypes?.has(schemaField.type)) return null;
  const Renderer = FIELD_RENDERERS[schemaField.type] || TextField;
  const isFullWidth = HALF_WIDTH_NAMES.has(schemaField.name)
    ? false
    : columns === 1 || FULL_WIDTH_TYPES.has(schemaField.type);
  const isReadOnly = mergedReadOnlyFields?.has(schemaField.name) ?? false;

  return (
    <Grid
      item
      xs={12}
      md={isFullWidth ? 12 : 6}
      key={schemaField.name}
      data-field-name={schemaField.name}
    >
      <Controller
        control={form.control}
        name={schemaField.name}
        rules={buildFieldRules(schemaField)}
        render={({ field: rhfField }: { field: {
          name: string;
          value: unknown;
          onChange: (value: unknown) => void;
          onBlur: () => void;
        } }) => (
          <FieldGroup
            name={schemaField.name}
            label={schemaField.resolvedLabel}
            required={!!schemaField.required}
            helperText={schemaField.helperText}
            tooltip={resolveTooltip(schemaField)}
            hideLabel={schemaField.type === "boolean"}
          >
            {isReadOnly ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  color: "text.secondary",
                  fontSize: 14,
                  userSelect: "none",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {formatValue(schemaField, rhfField.value) ?? "—"}
              </Box>
            ) : (
              <Renderer
                field={schemaField}
                value={rhfField.value}
                name={rhfField.name}
                onChange={rhfField.onChange}
                onBlur={rhfField.onBlur}
              />
            )}
          </FieldGroup>
        )}
      />
    </Grid>
  );
}

function renderFlatGrid(args: RenderCommon): React.ReactNode {
  return (
    <Grid container columnSpacing={3} rowSpacing={2.5}>
      {args.fields.map((f) => fieldItem(f, args))}
    </Grid>
  );
}

function renderSectioned(
  args: RenderCommon & {
    sections: import("@/types/api/formSchema").FormMetadataSection[];
    sectionExtras: Record<string, React.ReactNode> | undefined;
  },
): React.ReactNode {
  const { fields, sections, sectionExtras } = args;
  const bySection = new Map<string, ResolvedField[]>();
  for (const f of fields) {
    const key = f.section ?? "other";
    if (!bySection.has(key)) bySection.set(key, []);
    bySection.get(key)!.push(f);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {sections.map((section) => {
        const inSection = bySection.get(section.key) ?? [];
        const extra = sectionExtras?.[section.key];
        if (inSection.length === 0 && !extra) return null;
        const sectionColumns = (section.columns as 1 | 2 | undefined) ?? args.columns;
        const beforeFields = extra ? inSection.filter((f) => !f.after_extra) : inSection;
        const afterFields = extra ? inSection.filter((f) => f.after_extra) : [];
        const gridFor = (list: ResolvedField[]) => (
          <Grid container columnSpacing={3} rowSpacing={2.5}>
            {list.map((f) =>
              fieldItem(f, { ...args, columns: sectionColumns }),
            )}
          </Grid>
        );
        if (section.style === "flat") {
          return (
            <Box key={section.key}>
              {extra}
              {gridFor(inSection)}
            </Box>
          );
        }
        return (
          <Box
            key={section.key}
            sx={{
              border: 1,
              borderColor: "#E0E4EF",
              borderRadius: "8px",
              p: 2,
            }}
          >
            {section.title && (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 1.5,
                  color: "text.primary",
                }}
              >
                {section.title}
              </Typography>
            )}
            {gridFor(beforeFields)}
            {extra && <Box sx={{ mt: 2 }}>{extra}</Box>}
            {afterFields.length > 0 && (
              <Box sx={{ mt: 2 }}>{gridFor(afterFields)}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
